import { ActionIcon, Indicator, Tooltip } from "@mantine/core";
import { IconUserExclamation } from "@tabler/icons-react";
import { useMemo } from "react";
import type { AssignmentState } from "../../types/casePathologistAssignment";

interface StalePathologistAssignmentIndicatorProps {
  assignment: AssignmentState;
}

/**
 * Header indicator shown when a save was rejected because the case now belongs
 * to another pathologist. The pulsing dot draws the eye without displacing the
 * case content the way the old full-width banner did; the rejection detail
 * lives in the tooltip.
 */
export default function StalePathologistAssignmentIndicator({
  assignment,
}: StalePathologistAssignmentIndicatorProps) {
  const detailLabel = useMemo(
    () =>
      `This case was reassigned — ${assignment.pathologist.fullName} is now ` +
      `the current pathologist (revision ${assignment.revision}). Your unsaved ` +
      `changes remain on this device and were not accepted. Copy them before ` +
      `leaving, or refresh after reviewing the current owner.`,
    [assignment.pathologist.fullName, assignment.revision]
  );

  return (
    <Tooltip
      label={detailLabel}
      multiline
      maw={340}
      position="bottom-start"
      offset={8}
      withinPortal
      events={{ hover: true, focus: true, touch: true }}
    >
      <Indicator processing color="red" size={9} offset={3}>
        <ActionIcon
          variant="light"
          color="red"
          size="md"
          radius="xl"
          aria-label={detailLabel}
        >
          <IconUserExclamation size={16} />
        </ActionIcon>
      </Indicator>
    </Tooltip>
  );
}
